import * as React from 'react'

import { countriesOrder } from 'configuration/phone-number-input'

import {
  AppointmentFormTypes,
  AppointmentUtils,
  BookAppointmentFormLayout,
  Components,
  Containers,
} from '@grandvisionhq/appointments'
import { Button, Delayed } from '@grandvisionhq/common'
import { Text } from '@grandvisionhq/elements'
import {
  ComposedForm,
  DateOfBirth,
  FormControl,
  FormTypes,
  PhoneNumberInput,
  Util,
} from '@grandvisionhq/forms'
import { Types } from '@grandvisionhq/graphql'
import { useIntl } from '@grandvisionhq/state'

import { consents, INTL_DEFAULTS, optIns } from '../../../configuration'

const { ConsentsAndOptIns, OptionalFieldset, CheckboxField } = Components
const { SignInBox } = Containers

const BookAppointmentForm: AppointmentFormTypes.FormComponent = ({
  layout,
  loggedIn,
  onChange,
  submitting = false,
  formAlert = null,
  disableSubmit = false,
  beforeSubmitSlot = null,
  ...rest
}) => {
  const { getLabel, getFormattedLabel } = useIntl()

  const onChangeHandler = React.useCallback(
    (state: FormTypes.ComposedFormState) => {
      onChange?.(state)
    },
    [onChange]
  )

  return (
    <ComposedForm {...rest} className="book-appointment-form" onChange={onChangeHandler}>
      {!loggedIn && <SignInBox />}
      {layout === BookAppointmentFormLayout.CHILD && (
        <OptionalFieldset
          labels={{ legend: getLabel('appointments.component.bookAppointmentForm.legend.child') }}
          active={layout === BookAppointmentFormLayout.CHILD}
        >
          <ComposedForm.Part name="childDetails">
            <FormControl
              {...FormControl.defaultProps}
              id="childFirstName"
              data-t="child-first-name"
              label={getLabel('appointments.component.bookAppointmentForm.label.child.firstName')}
              validations={[Util.validations.isText, Util.validations.maxLength(30)]}
              errorMessage={getLabel(
                'appointments.component.bookAppointmentForm.error.child.firstName'
              )}
            />
            <FormControl
              {...FormControl.defaultProps}
              id="childLastName"
              data-t="child-last-name"
              label={getLabel('appointments.component.bookAppointmentForm.label.child.lastName')}
              validations={[Util.validations.isText, Util.validations.maxLength(255)]}
              errorMessage={getLabel(
                'appointments.component.bookAppointmentForm.error.child.lastName'
              )}
            />
            <DateOfBirth {...FormControl.defaultProps} name="childDateOfBirth" required={true} />
          </ComposedForm.Part>
        </OptionalFieldset>
      )}
      <OptionalFieldset
        labels={{
          legend: getLabel('appointments.component.bookAppointmentForm.legend.personalDetails'),
        }}
        active={layout === BookAppointmentFormLayout.CHILD}
      >
        <ComposedForm.Part name="personalDetails">
          <FormControl
            {...FormControl.defaultProps}
            id="firstName"
            data-t="personal-details-first-name"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.personalDetails.firstName'
            )}
            validations={[Util.validations.isText, Util.validations.maxLength(30)]}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.personalDetails.firstName'
            )}
          />
          <FormControl
            {...FormControl.defaultProps}
            id="lastName"
            data-t="personal-details-last-name"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.personalDetails.lastName'
            )}
            validations={[Util.validations.isText, Util.validations.maxLength(255)]}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.personalDetails.lastName'
            )}
          />
          <DateOfBirth {...FormControl.defaultProps} name="dateOfBirth" required={false} />
        </ComposedForm.Part>
      </OptionalFieldset>
      <OptionalFieldset
        labels={{
          legend: getLabel('appointments.component.bookAppointmentForm.legend.contactDetails'),
        }}
        active={layout === BookAppointmentFormLayout.CHILD}
      >
        <ComposedForm.Part name="contactDetails">
          <FormControl
            {...FormControl.defaultProps}
            id="email"
            type="email"
            data-t="contact-details-email"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.contactDetails.email'
            )}
            disabled={loggedIn}
            validations={[Util.validations.isEmailAddress]}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.contactDetails.email'
            )}
          />
          <PhoneNumberInput
            {...FormControl.defaultProps}
            id="mobilePhone"
            data-t="contact-details-mobile-phone"
            label={getLabel(
              'appointments.component.bookAppointmentForm.label.contactDetails.mobilePhone'
            )}
            errorMessage={getLabel(
              'appointments.component.bookAppointmentForm.error.contactDetails.mobilePhone'
            )}
            defaultCountry={INTL_DEFAULTS.country}
            localeForTranslations={INTL_DEFAULTS.locale}
            countriesOrder={countriesOrder}
          />
        </ComposedForm.Part>
      </OptionalFieldset>
      {!loggedIn && (
        <ComposedForm.Part name="consentsAndOptIns">
          <ConsentsAndOptIns
            className="book-appointment-form__spacing-top"
            errorMessage={getLabel('appointments.component.consentsAndOptIns.error')}
            consents={consents}
            optIns={optIns}
          />
        </ComposedForm.Part>
      )}
      {layout === BookAppointmentFormLayout.CHILD && (
        <Text className="book-appointment-form__spacing-top">
          {getFormattedLabel('appointments.component.bookAppointmentForm.additionalCaption', {
            parseLink: true,
            openLinksInNewTab: true,
          })}
        </Text>
      )}
      {formAlert}
      {beforeSubmitSlot}
      <Button
        type="submit"
        caption={getLabel('appointments.component.bookAppointmentForm.submit')}
        isLoading={submitting}
        disabled={disableSubmit}
        className="book-appointment-form__spacing-top"
      />

      {submitting && (
        <Delayed delay={2500} scrollIntoView className="book-appointment-form__spacing-top">
          <Text
            color="text-secondary"
            align="center"
            className="book-appointment-form__spacing-top"
          >
            {getLabel('appointments.component.bookAppointmentForm.slowLoadingIndication')}
          </Text>
        </Delayed>
      )}
    </ComposedForm>
  )
}

BookAppointmentForm.fromDataToState = (data, { layout }) => {
  let state: FormTypes.ComposedFormValueState = {}

  const child: Record<string, string | number> = {
    childFirstName: '',
    childLastName: '',
    childDateOfBirth: '',
  }
  const personalDetails: Record<string, string | number> = {
    firstName: data.defaultBillingAddress?.firstName ?? data.personalDetails?.firstName ?? '',
    lastName: data.defaultBillingAddress?.lastName ?? data.personalDetails?.lastName ?? '',
    dateOfBirth: data.personalDetails?.dateOfBirth,
  }
  const contactDetails: Record<string, string | number> = {
    email: data.email,
    mobilePhone: data.personalDetails?.phone ?? '',
  }

  switch (layout) {
    case BookAppointmentFormLayout.CHILD:
      state = Util.mergeComposedFormValueState(state, {
        childDetails: Util.fieldsToFormState(child),
      })
    default:
      state = Util.mergeComposedFormValueState(state, {
        personalDetails: Util.fieldsToFormState(personalDetails),
      })
      state = Util.mergeComposedFormValueState(state, {
        contactDetails: Util.fieldsToFormState(contactDetails),
      })
  }

  return state
}

BookAppointmentForm.getFormGroups = ({ layout }: AppointmentFormTypes.GetFormGroups): string[] =>
  layout === BookAppointmentFormLayout.CHILD
    ? ['childDetails', 'personalDetails', 'contactDetails', 'consentsAndOptIns']
    : ['personalDetails', 'contactDetails', 'consentsAndOptIns']

BookAppointmentForm.fromStateToData = (state, { layout, intl }, metadata) => {
  const data = Util.getValuesFromComposedState(state)
  const optInsAreDisplayed =
    data.consentsAndOptIns && 'subscribe-newsletter' in data.consentsAndOptIns
  const consentsAreDisplayed =
    data.consentsAndOptIns && 'termsConditionsAndPolicies' in data.consentsAndOptIns
  const termsAndConditionsConsent = consents.find(
    (consent) => consent.value === 'termsConditionsAndPolicies'
  )
  const optIns = termsAndConditionsConsent?.children ?? []

  const result: AppointmentFormTypes.FormStateData = {
    child:
      layout === BookAppointmentFormLayout.CHILD
        ? {
            firstName: data.childDetails?.childFirstName ?? '',
            lastName: data.childDetails?.childLastName ?? '',
            dateOfBirth: Util.toStandardDateFormat(data.childDetails?.childDateOfBirth) ?? '',
          }
        : undefined,
    personalDetails: {
      firstName: data.personalDetails?.firstName ?? '',
      lastName: data.personalDetails?.lastName ?? '',
      dateOfBirth: Util.toStandardDateFormat(data.personalDetails?.dateOfBirth),
      email: data.contactDetails?.email ?? '',
      phone: data.contactDetails?.mobilePhone ?? '',
      billingAddress: {
        firstName: data.personalDetails?.firstName ?? '',
        lastName: data.personalDetails?.lastName ?? '',
      },
    },
    optins:
      optInsAreDisplayed && !metadata?.loggedIn
        ? [
            {
              id: 'subscribe-newsletter',
              value: data.consentsAndOptIns?.['subscribe-newsletter'] === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('subscribe-newsletter', optIns) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
          ]
        : null,
    consents:
      consentsAreDisplayed && !metadata?.loggedIn
        ? [
            {
              consentName: 'terms-conditions',
              value: data.consentsAndOptIns?.termsConditionsAndPolicies === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('termsConditionsAndPolicies', consents) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
          ]
        : null,
    customerComment: null,
    preferredChannelOfCommunication: null,
  }

  return result
}

export default BookAppointmentForm
